import React, { useState, useEffect } from 'react';

const SquarePaymentForm = ({ config, amount, currency, onSuccess, onError, loading, setLoading }) => {
  const [cardValid, setCardValid] = useState(false);
  const [squareLoaded, setSquareLoaded] = useState(false);
  const [card, setCard] = useState(null);

  useEffect(() => {
    const loadSquareSDK = async () => {
      if (window.Square) {
        setSquareLoaded(true);
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://sandbox.web.squarecdn.com/v1/square.js';
      if (config.environment === 'production') {
        script.src = 'https://web.squarecdn.com/v1/square.js';
      }
      script.async = true;
      script.onload = () => setSquareLoaded(true);
      document.body.appendChild(script);
    };

    loadSquareSDK();
  }, [config.environment]);

  useEffect(() => {
    if (!squareLoaded || !window.Square || card) return;

    const initializeSquare = async () => {
      try {
        const payments = window.Square.payments(config.applicationId, config.locationId);
        const cardElement = await payments.card();
        await cardElement.attach('#card-container');

        cardElement.addEventListener('change', (e) => {
          setCardValid(e.isValid);
        });

        setCard(cardElement);
      } catch (error) {
        onError('Failed to initialize Square payment form');
      }
    };

    initializeSquare();
  }, [squareLoaded, config, card, onError]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!card || !cardValid) {
      onError('Please enter valid card details');
      return;
    }

    setLoading(true);
    onError(null);

    try {
      const amountInCents = Math.round(parseFloat(amount) * 100);

      // Create payment intent on backend
      const intentResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/create-payment-intent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount: amountInCents,
          currency: currency || 'usd',
          metadata: { description: 'MetClawPolis Payment' },
        }),
      });

      if (!intentResponse.ok) throw new Error('Failed to create payment intent');

      const { idempotencyKey } = await intentResponse.json();

      // Tokenize card
      const result = await card.tokenize();

      if (result.status === 'OK') {
        // Complete payment on backend
        const completeResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/complete-payment`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            sourceId: result.token,
            idempotencyKey,
            amount: amountInCents,
            currency: currency || 'usd',
          }),
        });

        const paymentResult = await completeResponse.json();

        if (completeResponse.ok && paymentResult.status === 'COMPLETED') {
          onSuccess(paymentResult);
        } else {
          onError(paymentResult.error || 'Payment failed');
        }
      } else {
        onError(result.errors?.[0]?.message || 'Card tokenization failed');
      }
    } catch (err) {
      onError(err.message || 'An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="payment-form__form">
      <div className="form-group">
        <label>Card Details</label>
        <div id="card-container" className="card-element-wrapper" />
      </div>

      <button type="submit" disabled={!cardValid || loading} className="payment-button">
        {loading ? 'Processing...' : `Pay $${amount}`}
      </button>
    </form>
  );
};

export default SquarePaymentForm;
